import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  IconButton,
  InputBase,
  Button,
  Pagination,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  CardHeader,
  Avatar,
  Typography,
  Stack,
  Modal,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from 'prop-types';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import makeStyles from "@material-ui/styles/makeStyles";
import Loader from "react-js-loader";

import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from '../../components/layout/ConfirmModal';
import FilterButton from "../../components/layout/FilterButton";
import WelcomeBookModal from "../../components/layout/WelcomeBookModal";
import {ReactComponent as ViewIcon} from '../../assets/img/view.svg';
import {ReactComponent as DeleteIcon} from '../../assets/img/delete.svg';
import { deleteBookings, searchBooking, sendWelcomePackMail } from "../../redux/actions/book";
import config from "../../config/config";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'client',
    numeric: false,
    disablePadding: true,
    label: 'Client',
  },
  {
    id: 'ocupation',
    numeric: false,
    disablePadding: false,
    label: 'Ocupation',
  },
  {
    id: 'preferredDate',
    numeric: false,
    disablePadding: false,
    label: 'Booking Date',
  },
  {
    id: 'duration',
    numeric: false,
    disablePadding: false,
    label: 'Duration',
  },
  {
    id: 'source',
    numeric: false,
    disablePadding: false,
    label: 'Source',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'onboard',
    numeric: false,
    disablePadding: false,
    label: 'Onboard?',
  },
];

function EnhancedTableHead(props) {
  const theme = useTheme();
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all users',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              fontSize: '0.8rem',
              color: theme.palette.mode ==="light" ? "#888888" : "#fff",
              '&:hover span': {
                color: theme.palette.mode ==="light" ? "#888888" : "#fff",
              },
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ fontWeight: 600 }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell
          align={'right'}
          padding={'normal'}
          sx={{
            fontSize: '12.5px',
            fontWeight: 600,
            color: theme.palette.mode ==="light" ? "#888888" : "#fff",
            '&:hover span': {
              color: theme.palette.mode ==="light" ? "#888888" : "#fff",
            },
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const BookTable = () => {
  const { loading, bookingData, user } = useSelector(state => ({
    loading: state.book.loading,
    bookingData: state.book.bookingData,
    user: state.auth.user,
  }));
  
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const [filterOption, setFilterOption] = useState(-1);
  const [filteredBookingData, setFilteredBookingData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [onBoardConfirmModalVisible, setOnBoardConfirmModalVisible] = useState(false);
  const [boardAgainConfirmModalVisible, setBoardAgainConfirmModalVisible] = useState(false);
  const [boardSuccessedModalVisible, setBoardSuccessedModalVisible] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showWelcomeBookModal, setShowWelcomeBookModal] = useState(false);

  const onCloseOnBoardConfirmModal = () => setOnBoardConfirmModalVisible(false);
  const onCloseBoardAgainConfirmModal = () => setBoardAgainConfirmModalVisible(false);
  const onCloseBoardSuccessedModal = () => setBoardSuccessedModalVisible(false);
  const onCloseWelcomeBookModal = () => setShowWelcomeBookModal(false);
  const handleClose = () => setOpen(false);

  const useStyles = makeStyles({
    title: {
      color: theme.palette.mode === 'light' ? '#555600' : '#fff',
      fontSize: '0.86rem',
      fontWeight: '500',
      lineHeight: '18px',
      '&:hover': {
        color: '#009ef7 !important',
      }
    },
    subheader: {
      color: theme.palette.mode === 'light' ? '#666300' : '#fff',
      fontSize: '0.7rem',
      lineHeight: '20px',
    }
  });
  const styles = useStyles();

  useEffect(() => {
    setSelectedBookingId(null);
  }, [bookingData.length]);

  useEffect(() => {
    if (bookingData && bookingData.length) {
      if (filterOption > -1) {
        const tempFiltered = bookingData.filter((e) => e.status == filterOption);
        setFilteredBookingData([ ...tempFiltered ]);
      } else {
        setFilteredBookingData([ ...bookingData ]);
      }
    }
  }, [bookingData, filterOption]);

  useEffect(() => {
    if (user?.id) {
      const dontShow = localStorage.getItem(user.id + "_DONT_SHOW_WELCOME_BOOK_TIP") || "false";
      if (dontShow === 'false') {
        setShowWelcomeBookModal(true);
      }
    }
  }, [user]);

  function getDurationText(duration) {
    return duration < 1 ? 'Custom' : duration > 1 ? duration + ' Hours' : duration + ' Hour';
  }

  const cancelDelete = () => {
    handleClose();
    setCancel(true);
    setSelectedBookingId(null);
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredBookingData.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(parseInt(event.target.value, 10));
  //   setPage(0);
  // };

  // const handleChangeDense = (event) => {
  //   setDense(event.target.checked);
  // };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty bookingData.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredBookingData.length) : 0;

  const handleChangePage = (event, page) => {
    setPage(page - 1);
  }

  const handleGoDetailPage = (bookId) => () => {
    navigate(`/bookings/details/${bookId}`);
  }

  const deleteBooking = () => {
    setConfirm(true);
    handleClose();
    dispatch(deleteBookings({ bookingIds: selectedBookingId ? [selectedBookingId] : selected }));
  }

  const handleSearchBooking = (event) => {
    dispatch(searchBooking({keyword: event.target.value}));
  }

  const onBoard = async () => {
    const res = await dispatch(sendWelcomePackMail({ bookingId: selectedBookingId }));
    onCloseOnBoardConfirmModal();
    onCloseBoardAgainConfirmModal();
    if (res) {
      setBoardSuccessedModalVisible(true);
    }
  }

  return (
    <Grid
      container
      justifyContent='center'
      sx={{
        mt: { sm: 3, md: 6 },
        width: '100%',
        borderRadius: 2,
        paddingY: 3,
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'rgba(25,25,30,0.5)'
      }}
    >
      <Grid
        item
        container
        direction='row'
        justifyContent='space-between'
        sx={{ width: '95%', pt: 1 }}
      >
        <Paper
          sx={{
            p: '0px 0px 0px 4px',
            display: 'flex',
            backgroundColor: theme.palette.mode === 'light' ? '#eef3f7' : '#1b1b29',
            boxShadow: 'none'
          }}
        >
          <IconButton sx={{ p: '0px 10px' }} aria-label="menu">
            <SearchIcon />
          </IconButton>
          <InputBase
            placeholder="Search bookings"
            inputProps={{ sx: { fontSize: '0.85rem', fontWeight: 400, padding: '3px 0px 5px' } }}
            onChange={handleSearchBooking}
            sx={{
              ml: 1,
              flex: 1,
              '& input::placeholder': {
                fontSize: '0.85rem',
                fontWeight: 400
              }
            }}
          />
        </Paper>
        {selected.length > 0 ?
          <Stack direction='row' sx={{ alignItems: 'center' }}>
            <Typography>{selected.length} selected</Typography>
            <Button
              variant="contained"
              color="error"
              sx={{ color: '#fff', fontWeight: 600, padding: '10px 30px', textTransform: 'none', marginInlineStart: 3 }}
              onClick={() => setOpen(true)}
            >
              Delete Selected
            </Button>
          </Stack> :
          <FilterButton sx={{ padding: '10px 30px' }} onClick={(value) => setFilterOption(value)} />
        }
      </Grid>

      <Paper sx={{ width: '95%', mb: 2.5, mt: 2.5, boxShadow: 'none' }}>
        {loading ? (
          <Grid marginTop="50px" marginBottom="50px">
            <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
          </Grid>
        ) : (
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={filteredBookingData.length}
              />
              <TableBody>
                {filteredBookingData.length > 0 && stableSort(filteredBookingData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={index}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                            onClick={(event) => handleClick(event, row.id)}
                          />
                        </TableCell>
                        <TableCell
                          id={labelId}
                          scope="row"
                          padding="none"
                          sx={{ height: '75px' }}
                        >
                          <CardHeader
                            classes={{ title: styles.title, subheader: styles.subheader }}
                            avatar={row.selfiePhotoThumbnail ?
                              <Box
                                component="img"
                                src={`${config.SERVER_BASE_URL}/${row.selfiePhotoThumbnail}`}
                                alt="Client Selfie Thumbnail"
                                sx={{
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  transform: 'translate3d(0, 0, 1px)',
                                }}
                              /> :
                              <Avatar sx={{ height: '3rem', width: '3rem' }}>
                                {row.fullName[0]}
                              </Avatar>
                            }
                            title={row.fullName}
                            subheader={row.client.email}
                            sx={{ padding: 0 }}
                          />
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.client.occupation}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{moment(row.preferredDate).format('DD MMM YYYY')}, {moment(row.preferredDate).format('LT')}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{getDurationText(row.duration)}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>{row.client.searchEngine}</TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', color: theme.palette.mode ==="light" ? "#555400" : "#fff" }}>
                          {row.status === 0 ?
                            row.isExpired ?
                              <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434" }}>Expired</Box> :
                              <Box className="booking-detail-type" sx={{ color: '#FFC700', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28" }}>Pending</Box> :
                            row.status === 1 ?
                              <Box className="booking-detail-type" sx={{ color: '#F1416C', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434" }}>Declined</Box> :
                              <Box className="booking-detail-type" sx={{ color: '#50CD89', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238" }}>Approved</Box>
                          }
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem' }}>
                          {row.status === 2 ?
                            row.isExpired ?
                              <Box
                                className="booking-detail-type"
                                sx={{ color: '#50CD89', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238" }}
                              >
                                Completed
                              </Box> :
                              row.onBoard ?
                                <Button
                                  variant="contained"
                                  sx={{
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    backgroundColor: theme.palette.mode ==="light" ? "#50d08a" : "#50cd89",
                                    textTransform: 'none'
                                  }}
                                  onClick={() => {
                                    setSelectedBookingId(row.id);
                                    setBoardAgainConfirmModalVisible(true);
                                  }}
                                >
                                  Onboarded
                                </Button> :
                                <Button
                                  variant="contained"
                                  sx={{
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    backgroundColor: "#7239ea",
                                    textTransform: 'none'
                                  }}
                                  onClick={() => {
                                    setSelectedBookingId(row.id);
                                    setOnBoardConfirmModalVisible(true);
                                  }}
                                >
                                  Onboard
                                </Button> :
                            null
                          }
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '0.8rem', paddingRight: '4px' }}>
                          <Stack direction='row' justifyContent="flex-end">
                            <Tooltip title="View" placement="top" enterTouchDelay={0}>
                              <IconButton
                                sx={{
                                  color: '#a1a5b7',
                                  "&:hover": {
                                    backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
                                    borderRadius: '6px',
                                  },
                                  "&:hover svg": {
                                    color: '#009ef7',
                                  },
                                }}
                                onClick={handleGoDetailPage(row._id)}
                              >
                                <ViewIcon style={{ width: '22px', height: '22px' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" placement="top" enterTouchDelay={0}>
                              <IconButton
                                sx={{
                                  color: '#a1a5b7',
                                  "&:hover": {
                                    backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
                                    borderRadius: '6px',
                                  },
                                  "&:hover svg": {
                                    color: '#009ef7',
                                  },
                                }}
                                onClick={() => {
                                  setSelectedBookingId(row.id);
                                  setOpen(true);
                                }}
                              >
                                <DeleteIcon style={{ width: '22px', height: '22px' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {filteredBookingData.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 33 * rowsPerPage }}>
            <Typography>You currently have no bookings.</Typography>
          </Box>
        )}
        {/* <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={bookingData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        /> */}
      </Paper>

      <Grid sx={{ mt: 1, width: '95%' }} container justifyContent='space-between' alignItems="center">
        <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
          Displaying {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, filteredBookingData.length)} of {filteredBookingData.length} entries
        </Typography>
        <Pagination
          count={Math.ceil(filteredBookingData.length / rowsPerPage)}
          shape="rounded"
          color="primary"
          onChange={handleChangePage}
          sx={{
            "& .MuiPaginationItem-root": {
              '&.Mui-selected': {
                color: '#fff'
              }
            }
          }}
        />
      </Grid>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '88%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#f1416c" }}>
              <Grid className="swal-i-content" sx={{ fontSize: '3em', color: '#f1416c' }}>!</Grid>
            </Grid>
            <Typography sx={{ fontSize: '1.8rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.2', mt: 3 }}>
              Are you sure?
            </Typography>
            <Typography id="modal-modal-description" sx={{ fontSize: '0.85rem', mt: 2, textAlign: 'center' }}>
              These booking records will be permanently deleted. This action cannot be undone.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="success" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }} onClick={deleteBooking}>Confirm</Button>
              <Button color="error" variant="contained" sx={{ marginInlineStart: 1, fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }} onClick={handleClose}>Cancel</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Modal
        open={confirm}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '88%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#50CD89" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '2.75em', color: '#50CD89' }}>&#10004;</Grid>
            </Grid>
            <Typography sx={{ fontSize: '1.8rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.2', mt: 3 }}>
              Success
            </Typography>
            <Typography id="modal-modal-description" sx={{ fontSize: '0.85rem', mt: 2, textAlign: 'center' }}>
              The selected bookings have been successfully deleted.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="primary" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }} onClick={() => setConfirm(false)}>Back to Bookings</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Modal
        open={cancel}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '88%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#F1416C" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '2.75em', color: '#F1416C' }}>&#x2715;</Grid>
            </Grid>
            <Typography sx={{ fontSize: '1.8rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.2', mt: 3 }}>
              Cancelled
            </Typography>
            <Typography id="modal-modal-description" sx={{ fontSize: '0.85rem', mt: 2, textAlign: 'center' }}>
              Selected customers were not deleted.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="primary" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }} onClick={() => setCancel(false)}>Back to Bookings</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <ConfirmModal
        visible={onBoardConfirmModalVisible}
        onClose={onCloseOnBoardConfirmModal}
        symbol="?"
        title="You are about to onboard this client."
        description="A Welcome Pack will be sent to ready them for their upcoming date. Do you wish to proceed?"
        titleYes="Confirm"
        titleNo="Cancel"
        onClickYes={onBoard}
        onClickNo={onCloseOnBoardConfirmModal}
      />
      <ConfirmModal
        visible={boardAgainConfirmModalVisible}
        onClose={onCloseBoardAgainConfirmModal}
        symbol="!"
        title="You have already onboarded this client."
        description="Another copy of your Welcome Pack will be sent to them. Do you wish to send it again?"
        titleYes="Confirm"
        titleNo="Cancel"
        onClickYes={onBoard}
        onClickNo={onCloseBoardAgainConfirmModal}
      />
      <ConfirmModal
        visible={boardSuccessedModalVisible}
        onClose={onCloseBoardSuccessedModal}
        symbol="✔"
        title="All done!"
        description="Your Welcome Pack has been sent to the client. You will be notified when it has been viewed."
        titleNo="Back to Bookings"
        onClickNo={onCloseBoardSuccessedModal}
      />
      <WelcomeBookModal
        visible={showWelcomeBookModal}
        onClose={onCloseWelcomeBookModal}
        id={user?.id}
      />
    </Grid>
  );
}

export default BookTable;